<template>
  <div>
    <Head :title="`${form.first_name} ${form.last_name}`" />
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb">
        <li class="breadcrumb-item"><Link href="/customers">Customers</Link></li>
        <li class="breadcrumb-item active" aria-current="page">{{ form.first_name }} {{ form.last_name }}</li>
      </ol>
    </nav>
    <h1>
      {{ form.first_name }} {{ form.middle_name }} {{ form.last_name }}
    </h1>

    <trashed-message v-if="customers.deleted_at" class="mb-6" @restore="restore"> This customer has been deleted. </trashed-message>

    <div class="row">
      <div class="col-md-6">
        <div class="content-box mb-3">
          <p class="lead">Customer details</p>
          <table class="table">
            <tbody>
              <tr>
                <td>Company</td>
                <th scope="row">{{ customers.company }}</th>
              </tr>
              <tr>
                <td>Name</td>
                <th scope="row">{{ customers.title }} {{ customers.first_name }} {{ customers.middle_name }} {{ customers.last_name }}</th>
              </tr>
              <tr>
                <td>Address</td>
                <th scope="row">{{ customers.address_1 }} <div v-if="customers.address_2">{{ customers.address_2 }}</div></th>
              </tr>
              <tr>
                <td>ZIP</td>
                <th scope="row">{{ customers.postal_code }}</th>
              </tr>
              <tr>
                <td>City</td>
                <th scope="row">{{ customers.city }}</th>
              </tr>
              <tr>
                <td>State</td>
                <th scope="row">{{ customers.state }}</th>
              </tr>
              <tr>
                <td>Country</td>
                <th scope="row"><span v-if="customers.country">{{ getCountry(customers.country)[0].name }}</span></th>
              </tr>
              <tr>
                <td>E-mail address</td>
                <th scope="row">{{ customers.email }}</th>
              </tr>
              <tr>
                <td>Website</td>
                <th scope="row">{{ customers.website }}</th>
              </tr>
              <tr>
                <td>Phone</td>
                <th scope="row">{{ customers.phone }}</th>
              </tr>
              <tr>
                <td>Cellphone</td>
                <th scope="row">{{ customers.cell_phone }}</th>
              </tr>
            </tbody>
          </table>

          <div class="actions-toolbar">
            <ShowLogs v-if="auth.user.role_admin" :logs="logs" />
            <Link :href="`/customers/${customers.id}/edit`" tabindex="-1" class="btn btn-primary">
              Edit Customer
            </Link>
            <button v-if="!customers.deleted_at" class="btn btn-outline-danger" tabindex="-1" type="button" @click="destroy">Delete Customer</button>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="content-box mb-3">
          <div class="passengers-list exists mb-3" v-if="passengers">
            <div class="existing rounded">
              <p class="lead">Known passengers</p>
              <div class="add-passenger">
                <EditPassenger v-for="(passenger, index) in passengers" :key="index" :passenger="passenger" :customers="customers"></EditPassenger>
              </div>
            </div>
          </div>
          <AddPassenger :customers="customers" />
        </div>
      </div>
    </div>

    <h2>Earlier Bookings</h2>

    <div class="content-box">
      <table class="table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Status</th>
            <th>Booking Date</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="booking in bookings" :key="booking.id">
            <td><Link :href="`/bookings/${booking.id}/edit`">{{ booking.id }}</Link></td>
            <td><Link :href="`/bookings/${booking.id}/edit`">{{ booking.status }}</Link></td>
            <td><Link :href="`/bookings/${booking.id}/edit`">{{ $filters.formatDate(booking.created_at) }}</Link></td>
          </tr>
          <tr v-if="!bookings">
            <td colspan="4">No bookings found.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import SelectCountry from '@/Shared/SelectCountry'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import PersonTitle from '@/components/Form/PersonTitle'
import Passenger from '@/components/Form/Passenger'
import AddPassenger from '@/components/AddPassenger'
import EditPassenger from '@/components/EditPassenger'
import Country from '@/Shared/Country.js'
import ShowLogs from '@/components/ShowLogs'

export default {
  components: {
    Head,
    Icon,
    Link,
    LoadingButton,
    SelectInput,
    SelectCountry,
    TextInput,
    TrashedMessage,
    PersonTitle,
    Passenger,
    AddPassenger,
    EditPassenger,
    Country,
    ShowLogs
  },
  layout: Layout,
  props: {
    customers: Object,
    passengers: Object,
    bookings: Object,
    logs: Object
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        company: this.customers.company,
        title: this.customers.title,
        first_name: this.customers.first_name,
        middle_name: this.customers.middle_name,
        last_name: this.customers.last_name,
        gender: this.customers.gender,
        dob: this.customers.dob,
        address_1: this.customers.address_1,
        address_2: this.customers.address_2,
        postal_code: this.customers.postal_code,
        city: this.customers.city,
        country: this.customers.country,
        email: this.customers.email,
        website: this.customers.website,
        phone: this.customers.phone,
        cell_phone: this.customers.cell_phone,
        passengers: this.passengers ? this.passengers : [],
      }),
      countries: Country
    }
  },
  computed: {
    auth() {
      return this.$page.props.auth
    }
  },
  methods: {
    update() {
      this.form.put(`/customers/${this.customers.id}`)
    },
    destroy() {
      if (confirm('Are you sure you want to delete this customer?')) {
        this.$inertia.delete(`/customers/${this.customers.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this customer?')) {
        this.$inertia.put(`/customers/${this.customers.id}/restore`)
      }
    },
    addPassenger() {
      this.form.passengers.push({
        sex: "",
        given_names: "",
        surname: "",
        dob: "",
        nationality: "",
        passport_number: "",
        passport_issue: "",
        passport_valid: "",
      });
    },
    removePassenger(index) {
      this.form.passengers.splice(index, 1);
    },
    getCountry(country) {
      return this.countries.filter(c => c.code == country)
    }
  },
}
</script>

<style lang="scss">
.passenger-item {
  padding: 1rem;
  margin-bottom: 1rem;
  background: $gray-200;
}

.btn-passenger {
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;

  &.expired {
    background: $red;
    border-color: $red;
  }
}
</style>
